import type { CommissionPlan } from './fees'
import type { BandSetting, InsuranceSetting } from './worker'
import type { ChartSpec } from './chart-spec'
import type { Schedule, ScheduleInput } from './schedule'
import type { CompactSetting, ContextUsage } from './compaction'

export type Role = 'user' | 'assistant' | 'system'

export type UserRole = 'admin' | 'user'

/** A safe user record (never includes password hash/salt). */
export interface User {
  id: string
  username: string
  name: string | null
  role: UserRole
  tokensIn: number
  tokensOut: number
  tokenLimit: number | null
  /** Per-user task quota overrides (null = role default: admins unlimited, users 2 daily / 10 occasional). */
  taskDailyLimit: number | null
  taskOccasionalLimit: number | null
  /** Max files the user may attach to a single message (null = DEFAULT_MAX_FILES). */
  maxFiles: number | null
  createdAt: number
  lastLogin: number | null
}

/** Default per-message attachment cap when a user has no explicit maxFiles override. */
export const DEFAULT_MAX_FILES = 10

export type SessionUser = Pick<User, 'id' | 'username' | 'name' | 'role' | 'maxFiles'>

/* ── Scheduled tasks (automations: an AI run or a reminder on a schedule) ──── */
/**
 * app = in the app only; telegram / both = the answer also goes to Telegram; auto = the AI decides
 * each run whether it is worth a Telegram message (for frequent checks that are usually quiet).
 */
export type TaskNotify = 'app' | 'telegram' | 'both' | 'auto'
/** ai = the AI does the instructions each run; remind = the instructions are sent as a reminder (no AI). */
export type TaskAction = 'ai' | 'remind'
/** chat = runs inside its chat, with that conversation as its context; new = a fresh chat each run (a report). */
export type TaskRunIn = 'chat' | 'new'
export interface Task {
  id: string
  title: string
  instructions: string
  schedule: Schedule
  /** The schedule in words ("Every 5 minutes during the US session"). */
  scheduleText: string
  action: TaskAction
  runIn: TaskRunIn
  /** The chat it runs in (runIn = chat). */
  chatId: string | null
  chatTitle: string | null
  notify: TaskNotify
  /** AI mode the task runs in (fast/auto/expert/heavy); null = the chat's own (in a chat) or expert (a report). */
  mode: ChatMode | null
  /** Model id (shared/models.ts registry); null = the chat's / app default. */
  model: string | null
  enabled: boolean
  /** user = made in the Tasks page; ai = the AI scheduled it in a chat. */
  createdBy: 'user' | 'ai'
  /** When it runs next (epoch ms); null when paused or finished. */
  nextRunAt: number | null
  runCount: number
  /** It has no more runs: its moments passed, its end or its max runs were reached. */
  doneAt: number | null
  lastRunAt: number | null
  createdAt: number
}
export interface TaskRun {
  id: string
  taskId: string
  chatId: string | null
  title: string
  summary: string
  /** skipped = not run (the AI budget was used up, the previous run was still going, or the server was down at the time). */
  status: 'ok' | 'error' | 'skipped' | 'quiet'
  read: boolean
  createdAt: number
}
/** Per-user task quotas (Grok-style): a few "always-on" daily slots + more occasional ones.
 *  `max` is null when unlimited (admins by default). */
export interface TaskLimits {
  daily: { used: number; max: number | null; unlimited: boolean }
  occasional: { used: number; max: number | null; unlimited: boolean } // runs less than daily
  /** AI wakeups today (scheduled tasks, watchers, scans, worker callbacks) against the daily budget. */
  wakes: { used: number; max: number; /** The user's own Grok subscription pays: no budget in force. */ unlimited?: boolean }
}
/** Create/update payload from the client. */
export interface TaskInput {
  title: string
  instructions: string
  schedule: ScheduleInput
  action?: TaskAction
  runIn?: TaskRunIn
  chatId?: string | null
  notify: TaskNotify
  mode?: ChatMode | null
  model?: string | null
  enabled?: boolean
}
/** A few upcoming runs of a schedule (the Tasks page preview), with its words and daily rate. */
export interface SchedulePreview {
  ok: boolean
  errors?: string[]
  text?: string
  runs?: number[]
  perDay?: number
}

/** The daily AI-wakeup budget and what the AI may schedule on its own (Settings → Automations). */
export interface AutomationSetting {
  /** AI runs a day that no one typed: scheduled tasks, watchers, scans, worker callbacks. */
  wakeBudget: number
  /** The AI may schedule its own follow-up checks in a chat (always visible and cancellable). */
  aiSchedule: boolean
  /** At most this many active AI-made schedules per chat. */
  aiPerChat: number
}
export const DEFAULT_AUTOMATION: AutomationSetting = { wakeBudget: 40, aiSchedule: true, aiPerChat: 8 }

/** Something the server tells an open app about (the /api/events stream). */
export type UserEvent =
  /** An automation woke the AI in a chat (start) and it answered (done). */
  | { type: 'wake'; chatId: string; phase: 'start' | 'done'; title: string }
  /** Tasks changed (made, edited, ran). */
  | { type: 'tasks' }
  /** Watchers / scans changed. */
  | { type: 'watchers' }
  /** Agents, their folders, group chats or their conversations changed (an agent wrote to another, an answer was left unread). */
  | { type: 'agents' }
  /** A copy of this user changed (an order, a fill, a skip, a stop). */
  | { type: 'copy'; copyId?: string }

/* ── Technical analysis (computed server-side from EODHD daily OHLC) ──────── */
export type TaAction =
  | 'buy'
  | 'sell'
  | 'neutral'
  | 'overbought'
  | 'oversold'
  | 'high-vol'
  | 'low-vol'
export type TaSummary = 'strong-sell' | 'sell' | 'neutral' | 'buy' | 'strong-buy'

export interface TaRow {
  name: string
  value: number | null
  action: TaAction
}
export interface MaRow {
  period: number
  sma: number | null
  smaAction: TaAction
  ema: number | null
  emaAction: TaAction
}
export interface PivotRow {
  name: string
  s3: number | null
  s2: number | null
  s1: number | null
  pivot: number | null
  r1: number | null
  r2: number | null
  r3: number | null
}
export interface TaBucket {
  summary: TaSummary
  buy: number
  sell: number
  neutral: number
}
export interface TechnicalAnalysis {
  symbol: string
  asOf: string // date of the last daily bar used
  price: number // last close used for the computation
  overall: TaSummary
  oscillators: TaBucket & { rows: TaRow[] }
  movingAverages: TaBucket & { rows: MaRow[] }
  pivots: PivotRow[]
  commentary: string[]
}

/** Company profile from EODHD fundamentals (best-effort; fields may be absent for ETFs/indices). */
export interface CompanyProfile {
  symbol: string
  name: string
  exchange: string | null
  currency: string | null
  type: string | null
  sector: string | null
  industry: string | null
  country: string | null
  description: string | null
  logo: string | null
  webUrl: string | null
  employees: number | null
  ipoDate: string | null
  marketCap: number | null
  peRatio: number | null
  eps: number | null
  dividendYield: number | null
  beta: number | null
  week52High: number | null
  week52Low: number | null
}

/** xAI team prepaid balance (read via the Management API). `configured` is false
 *  when no management key is set; `raw` is the unparsed xAI payload for transparency. */
export interface XaiBalance {
  configured: boolean
  ok: boolean
  balance: number | null
  currency: string | null
  /** This billing cycle's TOTAL usage cost so far (USD), before prepaid credit is applied. */
  spentThisCycle?: number | null
  /** What's actually OWED this cycle after prepaid credit + VAT (the real invoice). */
  owed?: number | null
  /** Prepaid credit consumed this cycle (USD). */
  prepaidApplied?: number | null
  raw: unknown
  error?: string
}

export interface AuthStatus {
  setupRequired: boolean
  user: SessionUser | null
}

/** A web/X source Grok cited during live research. */
export interface Citation {
  url: string
  title?: string
  snippet?: string
  source?: 'web' | 'x'
}

/** A surfaced tool action (calculator, memory write, live search, EODHD call). */
export interface ToolEvent {
  id: string
  /** 'calculate' | 'remember' | 'forget' | 'web_search' | 'x_search' | 'eodhd' | string */
  name: string
  label?: string
  status: 'running' | 'done' | 'error'
  input?: unknown
  output?: unknown
  /** Set when this event came from a sub-agent (multi-agent runs); undefined = root agent. */
  agentId?: string
  agentRole?: string
  depth?: number
}

/**
 * One ordered slice of a turn's activity, captured in the sequence it actually happened
 * so the timeline can render think → search → findings → think interleaved.
 * A reasoning block holds its own text; a tool slice points at a ToolEvent by id (the
 * event in `toolEvents` is the source of truth for its running/done state).
 */
export type ActivitySegment =
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool'; eventId: string }

export interface ChatMessage {
  id: string
  chatId: string
  role: Role
  /** Markdown; may contain ```chart fenced blocks rendered as live charts. */
  content: string
  citations?: Citation[]
  toolEvents?: ToolEvent[]
  /** Chronological interleave of reasoning + tool slices; absent on older messages. */
  segments?: ActivitySegment[]
  /** reasoning_effort used, model, token usage — for the assistant turn. */
  meta?: {
    model?: string
    reasoningEffort?: string
    /** The model mode this turn actually ran with (fast/expert/heavy). */
    mode?: ChatMode
    usage?: { input?: number; output?: number; cached?: number; costUsd?: number }
    /** The model's reasoning trace (for the collapsible "Thought for Ns" panel). */
    reasoning?: string
    /** Wall-clock ms from turn start to the first answer token — the "thinking" time. */
    thinkingMs?: number
    /** Wall-clock ms for the WHOLE turn (start → final message) — what "Worked for" shows. */
    durationMs?: number
    /** The user pressed Stop mid-answer; `content` is what had been written by then. */
    stopped?: boolean
    /** Who paid for this answer when the user has a Grok subscription connected. */
    billing?: { via: 'subscription' | 'api' | 'mixed'; switched?: string }
    /**
     * This message was not typed by the user: an automation woke the AI (the "user" line) or it is
     * the AI's answer to one. The chat shows it as a system line instead of a bubble.
     */
    trigger?: { kind: 'task' | 'worker' | 'watch' | 'scan' | 'agent'; id: string; title: string }
    /** Which agent wrote this answer (set in group chats, temporary chats and talks between agents). */
    agent?: { id: string; name: string }
    /** What the agent said at once, before it went to work out of sight ("on it"). */
    ack?: string
    /** A wakeup that found nothing worth the user's attention: logged, shown collapsed, left out of the AI's context. */
    quiet?: boolean
    /** How full the chat's context is after this answer — measured from the model's own token count. */
    context?: ContextUsage
  }
  createdAt: number
}

/**
 * Composer model mode (Grok-style). fast = plain grok, reasoning_effort low (default);
 * auto = pick low/high from the question; expert = reasoning_effort high;
 * heavy = high + our app-side multi-agent orchestration.
 */
export type ChatMode = 'fast' | 'auto' | 'expert' | 'heavy'
// Auto by default: thinks hard (high reasoning) on anything substantive, stays cheap
// (low) only on trivial small-talk. 'fast' is opt-in when the user wants speed over depth.
export const DEFAULT_CHAT_MODE: ChatMode = 'auto'

export interface Chat {
  id: string
  title: string
  pinned: boolean
  model: string | null
  /** Per-chat model mode; null = the default ('fast'). Changeable mid-chat. */
  mode?: ChatMode | null
  projectId: string | null
  createdAt: number
  updatedAt: number
  /** Messages with createdAt ≤ this have been compacted into a summary (the AI sees
   *  the summary instead of them). null = never compacted. The summary text itself
   *  stays server-side. */
  compactedAt: number | null
  /** Per-chat active Skill ids; null = use the user's default-on skills. */
  skillIds?: string[] | null
  /** Per-chat active MCP-connector ids; null = use the user's default-on connectors. */
  mcpIds?: string[] | null
  /** Set when this chat was spawned by a scheduled task run (hidden from the sidebar). */
  taskId?: string | null
  /** Trading switch (admin): the IBKR worker tools are attached to this chat's turns. */
  trading?: boolean
  /** Set when the conversation is a group chat's. */
  groupId?: string | null
  /** A temporary chat: nothing said in it is remembered, and it is gone after a day. */
  temp?: boolean
  /** The agents brought into a temporary chat. */
  tempWith?: string[]
  /** An answer in it has not been opened yet. */
  unread?: boolean
  /** A conversation one agent opened with another. */
  wire?: { from: string; to: string } | null
}

export interface Project {
  id: string
  name: string
  instructions: string | null
  createdAt: number
  updatedAt: number
}

/** A cross-chat search hit within a project. */
export interface MessageHit {
  messageId: string
  chatId: string
  chatTitle: string
  role: Role
  snippet: string
  createdAt: number
}

/** `topic` = a subject kept in the shared memory (a strategy, a plan): listed by name, read in full only when the conversation is about it. */
export type MemoryKind = 'portfolio' | 'watchlist' | 'preference' | 'fact' | 'topic'

/** Memory tiers: account-wide, per-project, or per-chat. */
export type MemoryScope = 'global' | 'project' | 'chat'

export interface Memory {
  id: string
  scope: MemoryScope
  scopeId: string | null
  kind: MemoryKind
  label: string
  value: unknown
  /** Put aside: kept, but not read by the agents. */
  off?: boolean
  createdAt: number
  updatedAt: number
}

export interface AppSettings {
  defaultModel: string
  reasoningEffort: 'none' | 'low' | 'medium' | 'high'
  theme: 'dark' | 'light'
  liveSearch: boolean
  /** Symbols shown in the top ticker tape (e.g. SPY.US, QQQ.US). */
  tickerSymbols: string[]
  /** Append a live total-portfolio-value pill to the ticker tape. */
  showPortfolioInTicker: boolean
  /** Also show each portfolio holding's symbol in the ticker tape. */
  showHoldingsInTicker: boolean
  /** Also show the user's watchlist symbols in the ticker tape. */
  showWatchlistInTicker: boolean
  /** Telegram destination chat ID (per-user). The bot token is stored too but is
   *  never returned to the client — `telegramConfigured` reflects whether it's set. */
  telegramChatId: string
  /** Stored bot token. GET sanitizes this to '' — write a new value on POST to set it. */
  telegramBotToken: string
  /** Read-only (GET-derived): a bot token is on file. */
  telegramConfigured?: boolean
  /** The user's IBKR Pro plan — fee estimates on worker and order cards. */
  commissionPlan: CommissionPlan
  /** Telegram messages when workers trade (fills, orders placed, closes, problems). */
  tradeNotify: boolean
  /** …including rule actions (stops moved, targets moved, entries cancelled). */
  tradeNotifyRules: boolean
  /** Default insurance for new workers (each worker can override it in chat). */
  insurance: InsuranceSetting
  /** Defaults for a worker's buy zone (each worker can override every one in chat). */
  band: BandSetting
  /** The AI-wakeup budget and self-scheduling limits. */
  automation: AutomationSetting
  /** Where long chats auto-compact (measured prompt tokens); null = the default (300k). */
  compactAt: CompactSetting | null
  /** Copy trading: people I invited may copy my trades (off unless switched on). */
  copy?: { allow: boolean }
  /** The language a new agent answers in (unset: the language the user writes in). */
  agentLang?: 'Türkçe' | 'English'
  /** The model the user's agents think with (an id from shared/models.ts); unset = the server's default. A conversation that chose its own keeps it. */
  model?: string | null
}

/* ── Portfolio ─────────────────────────────────────────────────── */

export interface Position {
  id: string
  symbol: string
  quantity: number
  /** Average cost per share. */
  costBasis: number
  stopLoss: number | null
  takeProfit: number | null
  createdAt: number
  updatedAt: number
}

export type OrderType = 'market' | 'limit'

export type TradeSide = 'buy' | 'sell'

export interface Transaction {
  id: string
  symbol: string
  side: TradeSide
  quantity: number
  price: number
  fee: number
  note: string | null
  createdAt: number
}

/* ── Market data (EODHD-normalized) ───────────────────────────── */

export interface OhlcBar {
  /** YYYY-MM-DD for daily, or UNIX seconds for intraday. */
  time: number | string
  open: number
  high: number
  low: number
  close: number
  volume?: number
}

export interface Quote {
  symbol: string
  price: number
  change: number
  changePct: number
  open: number
  high: number
  low: number
  previousClose: number
  volume: number
  timestamp: number
}

export interface SearchResult {
  code: string
  exchange: string
  symbol: string // CODE.EXCHANGE
  name: string
  type: string
  country?: string
  currency?: string
}

/** Pre-market / after-hours snapshot (US-only on EODHD). */
export interface ExtendedQuote {
  symbol: string
  price: number
  prevClose: number
  change: number
  changePct: number
  time: number // epoch ms of the last extended trade
}

/** Real-time trade tick from the EODHD WebSocket (US-only). */
export interface LiveQuote {
  symbol: string
  price: number
  time: number
  status: string // 'open' | 'closed' | 'extended hours'
}

/** A point in the portfolio value history (hourly snapshots). */
export interface PortfolioSnapshot {
  ts: number
  value: number
  cost: number
}

/* ── Chat streaming protocol (server → client over SSE) ────────── */

export type ChatStreamEvent =
  | { type: 'start'; messageId: string; chatId: string; /** Who is answering (a room has several speakers in one stream). */ agent?: { id: string; name: string } }
  | { type: 'reasoning'; delta: string }
  | { type: 'text'; delta: string }
  /**
   * The server discarded the answer text streamed so far — clear it. 'final': a preamble before
   * the real answer is written (normal flow, no notice); 'revise': the answer is being corrected.
   */
  | { type: 'text_reset'; reason: 'final' | 'revise' }
  | { type: 'tool'; event: ToolEvent }
  | { type: 'citations'; citations: Citation[] }
  /** Orb pointing directive: focus an on-screen element while speaking. */
  | { type: 'point'; targetId: string; tone?: 'up' | 'down' | 'neutral' }
  | { type: 'title'; title: string }
  | { type: 'message'; message: ChatMessage }
  /** The conversation was just auto-compacted; messages up to this point are now summarized. */
  | { type: 'compacted'; compactedAt: number; context?: ContextUsage }
  /** A one-line notice about the run itself (e.g. it moved from the Grok subscription to the API key). */
  | { type: 'notice'; text: string }
  | { type: 'error'; message: string }
  | { type: 'done' }

/** A file/image attached to a user message. */
export interface Attachment {
  name: string
  mime: string
  kind: 'image' | 'text' | 'binary'
  /** data URL for images AND binary uploads (data:<mime>;base64,…) */
  dataUrl?: string
  /** extracted text for text-ish files */
  text?: string
  /** for a persisted binary upload: where the sandbox sees it (/mnt/user-data/uploads/…) */
  path?: string
}

export interface SendMessageRequest {
  chatId: string
  message: string
  attachments?: Attachment[]
  reasoningEffort?: AppSettings['reasoningEffort']
  mode?: ChatMode
  /** Model id from shared/models.ts (grok-4.3 / grok-4.6); invalid values are ignored. */
  model?: string
  /** Trading switch for this chat (admin); persisted on the chat. */
  trading?: boolean
}

export type { ChartSpec }
